import { ReactElement } from "react";
import { Box } from "@twilio-paste/core/box";
import { Card } from "@twilio-paste/core/card";
import { Heading } from "@twilio-paste/core/heading";
import { Text } from "@twilio-paste/core/text";

import {
  EmptyStateOrientationOptions,
  EmptyStateProps,
  EmptyStateVariant,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import celebrationImage from "../../assets/emptyStateIllustrations/celebration-2.png";
import createImage from "../../assets/emptyStateIllustrations/create-2.png";
import dependencyImage from "../../assets/emptyStateIllustrations/dependency-2.svg";
import noDataImage from "../../assets/emptyStateIllustrations/noData-2.svg";
import noResultsImage from "../../assets/emptyStateIllustrations/noResults-2.svg";
import oopsImage from "../../assets/emptyStateIllustrations/oops-2.svg";
import unauthorizedImage from "../../assets/emptyStateIllustrations/unauthorized-2.svg";

import styles from "./EmptyState.module.css";

const variantToImageMap: Record<EmptyStateVariant, string> = {
  celebration: celebrationImage,
  create: createImage,
  dependency: dependencyImage,
  noData: noDataImage,
  noResults: noResultsImage,
  oops: oopsImage,
  unauthorized: unauthorizedImage,
};

// TODO: Migrate to new lib e2-commons/shared/ui
const EmptyState = ({
  borderless,
  children,
  description,
  heading,
  imageWidth = "15rem",
  maxWidth = "40rem",
  orientation = EmptyStateOrientationOptions.horizontal,
  variant,
}: EmptyStateProps): ReactElement => {
  const isHorizontal = orientation === EmptyStateOrientationOptions.horizontal;

  const renderEmptyStateContent = (): ReactElement => (
    <Box
      as="article"
      className={
        isHorizontal
          ? styles.emptyStateContainerHorizontal
          : styles.emptyStateContainerVertical
      }
      maxWidth={maxWidth}
    >
      <Box
        as="div"
        className={styles.emptyStateImageContainer}
        flex="1"
        width={imageWidth}
      >
        <Box
          as="img"
          className={styles.emptyStateImage}
          src={variantToImageMap[variant]}
          alt={`${variant} image`}
          width={imageWidth}
        />
      </Box>

      <Box flex="1">
        {heading && (
          <Heading as="h3" variant="heading30">
            {heading}
          </Heading>
        )}

        <Text as="p" dangerouslySetInnerHTML={{ __html: description }} />

        {children && (
          <Box className={styles.emptyStateRenderContainer}>{children}</Box>
        )}
      </Box>
    </Box>
  );

  if (borderless) {
    return renderEmptyStateContent();
  }

  return <Card>{renderEmptyStateContent()}</Card>;
};

export default EmptyState;
